import React, { useEffect, useState } from 'react';
import { LuCircleCheck } from 'react-icons/lu';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../../Context/authContext';
import { DownloadRistrictionContext } from '../../../../Context/downloadRistriction';
import stripeService from '../../../../services/stripeService';
import Toast from '../../../molecules/Toast';
import Button from '../../../molecules/Button';
import Skeleton from '../../skeleton/Skeleton';
import { PricingStyles, StyledCol } from './PricingSection.styles';
import { extraRows, findCurrentPlan, formatEur, limitRows, seatsLabel, termsNote } from '../planFormat';

function actionLabel(plan, isCurrent) {
  if (isCurrent) return 'Your plan';
  if (plan.seats.left === 0 && plan.key === 'lifetime') return 'Sold out';
  if (!plan.available) return 'Coming soon';
  return plan.key === 'lifetime' ? 'Get the lifetime deal' : `Start ${plan.trialDays}-day free trial`;
}

const PricingSection = () => {
  const { user, fetchUser } = useContextHook(AuthContext, v => ({ user: v.user, fetchUser: v.fetchUser }));
  const { downloadRistrictionModal } = useContextHook(DownloadRistrictionContext, v => ({
    downloadRistrictionModal: v.downloadRistrictionModal,
  }));
  const [plans, setPlans] = useState(null);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [unsubscribeClicked, setUnsubscribeClicked] = useState(false);

  useEffect(() => {
    stripeService
      .getAllPlans()
      .then(resp => setPlans(resp?.data?.plans || []))
      .catch(error => {
        setPlans([]);
        Toast({ type: 'error', message: error.message });
      });
  }, []);

  const subscription = user?.data?.subscriptionDetail;
  const hasSubscription = ['active', 'trialing'].includes(subscription?.status);
  const currentPlan = findCurrentPlan(plans || [], subscription);

  const handleChoose = async plan => {
    setCheckoutPlan(plan.key);
    try {
      const appOrigin = process.env.REACT_APP_ORIGIN;
      const resp = await stripeService.createCheckoutSession({
        plan: plan.key,
        successUrl: `${appOrigin}/ai-home`,
        cancelUrl: `${appOrigin}/ai-home`,
      });
      if (!resp?.data?.url) throw new Error('Checkout URL is missing. Please try again.');
      window.location.assign(resp.data.url);
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Something went wrong.' });
      setCheckoutPlan(null);
    }
  };

  const handleUnsubscribe = async () => {
    setUnsubscribeClicked(true);
    setUnsubscribeLoading(true);
    try {
      await stripeService.cancelSubscription();
    } catch (error) {
      Toast({ type: error?.requiresBillingUpdate ? 'info' : 'error', message: error.message });
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  useEffect(() => {
    const handleNotification = event => {
      const data = event.detail;
      if (unsubscribeClicked && data.actionType === 'subscription_canceled' && data.title === 'Subscription Deleted') {
        fetchUser();
        setUnsubscribeClicked(false);
      }
    };

    window.addEventListener('subscription-notification', handleNotification);
    return () => window.removeEventListener('subscription-notification', handleNotification);
  }, [unsubscribeClicked, fetchUser]);

  const monthly = plans?.find(plan => plan.key === 'monthly');

  return (
    <PricingStyles>
      <div className="section-head">
        <div className="text-box">
          {downloadRistrictionModal && (
            <div className="download-restriction">
              <strong className="title">Download Ristriction!</strong>
              <span className="text">
                You are not authorized to download lists. Please upgrade your plan to download lists.
              </span>
            </div>
          )}
          <strong className="title">Pay monthly, or pay once.</strong>
          <span className="subtitle">Both plans include all six tools. Prices in EUR.</span>
        </div>
      </div>

      <div className="pricing-holder">
        {plans ? (
          plans.map(plan => {
            const isCurrent = currentPlan?.key === plan.key;
            const label = actionLabel(plan, isCurrent);
            return (
              <StyledCol key={plan.key} $popularPlan={plan.key === 'lifetime'}>
                <div className="header">
                  {plan.key === 'lifetime' && <span className="tag">Best value</span>}
                  <strong className="title">{plan.name}</strong>
                  <span className="seats">{seatsLabel(plan)}</span>
                  <div className="price-box">
                    <strong className="price">{formatEur(plan.amount)}</strong>
                    <span className="timeline">{plan.interval ? '/month' : 'once'}</span>
                  </div>
                  <Button
                    rounded
                    className="plan-cta"
                    width="100%"
                    loader={checkoutPlan === plan.key}
                    disabled={isCurrent || !plan.available || Boolean(checkoutPlan)}
                    variant={isCurrent ? 'white' : undefined}
                    type="button"
                    onClick={() => handleChoose(plan)}>
                    {label}
                  </Button>
                  <span className="note">{termsNote(plan)}</span>
                </div>

                <dl className="limits">
                  {limitRows(plan).map(([name, value]) => (
                    <div className="limit" key={name}>
                      <dt>{name}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>

                <ul className="info-list">
                  {[...extraRows(plan), ...plan.tools].map(item => (
                    <li key={item}>
                      <LuCircleCheck className="tick-mark" />
                      <span className="text">{item}</span>
                    </li>
                  ))}
                </ul>
              </StyledCol>
            );
          })
        ) : (
          <>
            <Skeleton width="100%" height="423" radius="33px" />
            <Skeleton width="100%" height="423" radius="33px" />
          </>
        )}
      </div>

      {hasSubscription && (
        <Button
          width="200px"
          variant="danger"
          onClick={handleUnsubscribe}
          loader={unsubscribeLoading}
          disabled={!subscription?.priceId || unsubscribeLoading}>
          {subscription?.status === 'active' ? 'Unsubscribe Plan' : 'Cancel Subscription'}
        </Button>
      )}

      {monthly?.founding && (
        <div className="footer">
          <span className="text">
            When all {monthly.seats.total} founding seats are gone, the monthly price for new customers goes to{' '}
            {formatEur(monthly.regularAmount)}.
          </span>
        </div>
      )}
    </PricingStyles>
  );
};

export default PricingSection;
